import React from 'react';
import { sentenceCase } from 'change-case';
import { Table, Descriptions, Skeleton, Button } from 'antd';
import { Link, useRouteMatch } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import { Job, JobSummary, JOB_STATUS } from '../../types/Job';
import { Pagination } from '../../utils/PaginationHandler';

interface JobListInterfaceProps {
    wrapperStyleClass?: string;
    jobs: Job[] | JobSummary[];
    pagination: Pagination;
    isLoadingData: boolean;
    onPageChanges(filters: any, opts?: { isPaging: boolean }): void;
    onRelease(job: Job): Promise<void>;
}

const JobList: React.FC<JobListInterfaceProps> = (props: JobListInterfaceProps): React.ReactElement => {
    const { wrapperStyleClass, jobs, pagination, onPageChanges: setFiltersToApply, isLoadingData, onRelease } = props;

    const routeMatch = useRouteMatch();

    const columns: ColumnsType<JobSummary> = [
        {
            title: 'Middleware Id',
            dataIndex: 'id',
            key: 'id',
            width: '10%',
            responsive: ['xs', 'sm', 'md'],
            render(id: number): JSX.Element {
                return (
                    <Link to={`${routeMatch.path}/detail/${id}`} id="job-list__btn-view-detail" title="View detail">
                        {id}
                    </Link>
                );
            },
        },
        {
            title: 'Job Id',
            dataIndex: 'api_job_id',
            key: 'api_job_id',
            width: '10%',
            responsive: ['xs', 'sm', 'md'],
        },
        {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center',
            width: '10%',
            responsive: ['md'],
            render(createdAt: Date | string): string {
                return `${new Date(createdAt).toLocaleString()}`;
            },
        },
        {
            title: 'Pick up at',
            dataIndex: 'pickUpAt',
            key: 'pickUpAt',
            width: '10%',
            responsive: ['md'],
            render(pickUpAt: Date | string): string {
                return `${new Date(pickUpAt).toLocaleString()}`;
            },
        },
        {
            title: 'Client reference',
            dataIndex: 'clientReference',
            key: 'clientReference',
            width: '10%',
            responsive: ['md'],
            render(clientReference: string): JSX.Element {
                return <p style={{ whiteSpace: 'pre-wrap' }}>{clientReference}</p>;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'left',
            width: '10%',
            responsive: ['xs', 'sm', 'md'],
            render(status: string, record): JSX.Element {
                return (
                    <p className="m-0">
                        <span className="w-100 d-block mb-2">{sentenceCase(status)}</span>
                        {record.stackId && (
                            <Link
                                to={`/admin/stacks/detail/${record.stackId}`}
                                id={`stack-detail__btn-view-detail-${record.stackId}`}
                                title="View stack detail"
                            >
                                View stack detail
                            </Link>
                        )}
                    </p>
                );
            },
        },
        {
            title: 'Pick-up address',
            dataIndex: 'pickUpAddress',
            key: 'pickUpAddress',
            width: '15%',
            responsive: ['md'],
            render(pickUpAddress: string): JSX.Element {
                return <p style={{ whiteSpace: 'pre-wrap' }}>{pickUpAddress}</p>;
            },
        },
        {
            title: 'Drop-off address',
            dataIndex: 'dropOffAddress',
            key: 'dropOffAddress',
            width: '15%',
            responsive: ['md'],
            render(dropOffAddress: string): JSX.Element {
                return <p style={{ whiteSpace: 'pre-wrap' }}>{dropOffAddress}</p>;
            },
        },
        {
            title: 'Client',
            dataIndex: 'client',
            key: 'client',
            align: 'center',
            width: '10%',
            responsive: ['md'],
            render(client: { id: number; name: string }): JSX.Element {
                return <span>{client?.name}</span>;
            },
        },
        {
            title: 'Release job',
            key: 'release',
            align: 'center',
            width: '10%',
            responsive: ['md'],
            render(job: Job): JSX.Element | null {
                if (
                    ![JOB_STATUS.AWAITING_STACK_COMPLETION, JOB_STATUS.JOB_CREATION_FAILED].includes(
                        job.status as JOB_STATUS,
                    )
                ) {
                    return null;
                }

                return (
                    <Button
                        id="btn-release-job"
                        name="btn-release-job"
                        title="Release job"
                        onClick={(): Promise<void> => onRelease(job)}
                        className="ant-btn mr-2"
                    >
                        Release
                    </Button>
                );
            },
        },
    ];

    return (
        <div className={wrapperStyleClass || 'px-4 pb-4 mt-3 bg-white'}>
            <Descriptions size="small">
                <Descriptions.Item label={<span className="text-black-50">N&deg; jobs</span>}>
                    <span className="text-black-50">{pagination.totalItems}</span>
                </Descriptions.Item>
            </Descriptions>
            {jobs && (
                <Table
                    columns={columns}
                    dataSource={jobs as JobSummary[]}
                    tableLayout="fixed"
                    loading={isLoadingData}
                    pagination={{
                        pageSize: pagination.itemsPerPage,
                        current: pagination.pageNumber,
                        total: pagination.totalItems,
                        itemRender: (current, type, originalElement): string =>
                            pagination.uiControlsRenderer(current, type, originalElement),
                        showSizeChanger: false,
                        onChange: (page: number, pageSize?: number): void =>
                            setFiltersToApply(pagination.getPagingFilters(page, pageSize), { isPaging: true }),
                    }}
                />
            )}
        </div>
    );
};

export default JobList;
